import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Surface, Empty } from "../components/Primitives";
import {
  Plus, Trash2, Search, FileText, Truck, ClipboardCheck,
  CheckCircle2, X, Banknote, Eye, Filter, Receipt, Copy, Printer,
  Phone, Mail, MapPin, Calendar, History
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import RetroactiveOrderModal from "../components/RetroactiveOrderModal";

const STATUS = {
  brouillon: { label: "BROUILLON", cls: "badge-warn" },
  confirmee: { label: "CONFIRMÉE", cls: "badge-info" },
  preparee: { label: "PRÉPARÉE", cls: "badge-info" },
  livree: { label: "LIVRÉE", cls: "badge-success" },
  facturee: { label: "FACTURÉE", cls: "badge-success" },
  annulee: { label: "ANNULÉE", cls: "badge-alert" },
};

const TERMS_LABEL = {
  comptant: "Comptant", "30j": "30 jours", "60j": "60 jours", "90j": "90 jours", "1an": "1 an",
};

const fmtFCFA = (n) => `${(Math.round(n) || 0).toLocaleString("fr-FR")} F`;

function NewOrderForm({ products, customers, sites, onClose, onCreated, duplicate }) {
  const [form, setForm] = useState(() =>
    duplicate ? {
      customer_id: duplicate.customer_id, site_id: duplicate.site_id,
      payment_terms: duplicate.payment_terms, apply_tva: duplicate.apply_tva,
      notes: "", lines: duplicate.lines.map((l) => ({
        product_id: l.product_id, quantity: l.quantity,
        unit_price: l.unit_price, discount_percent: l.discount_percent || 0,
      })),
    } : {
      customer_id: "", site_id: sites[0]?.id || "", payment_terms: "comptant", apply_tva: false,
      notes: "", lines: [],
    }
  );
  const [search, setSearch] = useState("");

  const filteredProducts = useMemo(() => {
    if (!search) return products.slice(0, 30);
    return products.filter((p) =>
      [p.name, p.sku, p.category].join(" ").toLowerCase().includes(search.toLowerCase())
    ).slice(0, 50);
  }, [products, search]);

  const addLine = (p) => {
    if (form.lines.find((l) => l.product_id === p.id)) {
      toast.info(`${p.name} déjà dans la commande`);
      return;
    }
    setForm({ ...form, lines: [...form.lines, { product_id: p.id, quantity: 1, unit_price: p.price, discount_percent: 0 }] });
  };

  const updateLine = (i, key, value) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], [key]: Number(value) };
    setForm({ ...form, lines });
  };

  const delLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });

  const totals = useMemo(() => {
    let subtotal = 0;
    for (const l of form.lines) {
      subtotal += (l.quantity || 0) * (l.unit_price || 0) * (1 - (l.discount_percent || 0) / 100);
    }
    const tva = form.apply_tva ? subtotal * 0.18 : 0;
    return { subtotal, tva, total: subtotal + tva };
  }, [form.lines, form.apply_tva]);

  const submit = async () => {
    if (!form.customer_id) return toast.error("Choisissez un client");
    if (!form.lines.length) return toast.error("Ajoutez au moins un produit");
    try {
      const { data } = await api.post("/customer-orders", form);
      toast.success(`Commande ${data.order_number} créée`);
      onCreated();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex justify-center sm:p-4" onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-5xl shadow-2xl flex flex-col sm:rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="new-order-modal"
      >
        <div className="shrink-0 bg-white flex items-center justify-between p-4 sm:p-5 border-b border-[var(--border-line)]">
          <div className="min-w-0 flex-1 pr-2">
            <div className="font-heading font-extrabold text-lg sm:text-2xl truncate">Nouvelle commande client</div>
            <div className="label-mono mt-1 truncate">{duplicate ? `Dupliquée de ${duplicate.order_number}` : "Saisie manuelle"}</div>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-[var(--bg-soft,#f7f7f7)] flex items-center justify-center text-[var(--text-muted)] shrink-0" data-testid="close-new-order" aria-label="Fermer"><X size={22}/></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label-mono block mb-1.5">Client</label>
              <select data-testid="order-customer" required className="input-base" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
                <option value="">— Choisir —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Site</label>
              <select required className="input-base" value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })}>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Conditions paiement</label>
              <select className="input-base" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}>
                {Object.entries(TERMS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.apply_tva} onChange={(e) => setForm({ ...form, apply_tva: e.target.checked })} className="w-4 h-4 accent-[var(--primary)]" />
            <span>Appliquer TVA 18%</span>
          </label>

          {/* Product picker */}
          <div className="border border-[var(--border-line)] rounded-lg overflow-hidden">
            <div className="bg-[var(--bg-soft,#f7f7f7)] p-3 border-b border-[var(--border-line)]">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <input data-testid="product-search" className="input-base pl-9" placeholder="Rechercher un produit (SKU, nom, catégorie)..." value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
            <div className="max-h-44 overflow-y-auto divide-y divide-[var(--border-line)]">
              {filteredProducts.map((p) => (
                <button key={p.id} type="button" onClick={() => addLine(p)} className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-[var(--primary-soft,#F8E8F1)] transition-colors text-left">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{p.name}</div>
                    <div className="font-data text-xs text-[var(--text-muted)]">{p.sku} · {p.category || "—"}</div>
                  </div>
                  <div className="font-data text-right ml-3">
                    <div>{fmtFCFA(p.price)}</div>
                    <div className="text-xs text-[var(--text-muted)]">{p.unit}</div>
                  </div>
                  <Plus size={16} className="text-[var(--primary)] ml-2"/>
                </button>
              ))}
              {!filteredProducts.length && <div className="p-4 text-sm text-[var(--text-muted)] text-center">Aucun produit</div>}
            </div>
          </div>

          {/* Lines */}
          {form.lines.length > 0 && (
            <div className="border border-[var(--border-line)] rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-[var(--bg-soft,#f7f7f7)]">
                  <tr>
                    <th className="text-left p-2 label-mono">Produit</th>
                    <th className="text-right p-2 label-mono w-20">Qté</th>
                    <th className="text-right p-2 label-mono w-28">PU</th>
                    <th className="text-right p-2 label-mono w-20">Remise%</th>
                    <th className="text-right p-2 label-mono w-28">Total</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.lines.map((l, i) => {
                    const p = products.find((x) => x.id === l.product_id);
                    const lineTotal = (l.quantity || 0) * (l.unit_price || 0) * (1 - (l.discount_percent || 0) / 100);
                    return (
                      <tr key={l.product_id || `form-line-${i}`} className="border-t border-[var(--border-line)]">
                        <td className="p-2"><div className="font-medium">{p?.name}</div><div className="font-data text-xs text-[var(--text-muted)]">{p?.sku}</div></td>
                        <td className="p-1"><input data-testid={`qty-${i}`} type="number" min="1" value={l.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} className="input-base text-right py-1 w-full" /></td>
                        <td className="p-1"><input type="number" value={l.unit_price} onChange={(e) => updateLine(i, "unit_price", e.target.value)} className="input-base text-right py-1 w-full" /></td>
                        <td className="p-1"><input type="number" min="0" max="100" value={l.discount_percent} onChange={(e) => updateLine(i, "discount_percent", e.target.value)} className="input-base text-right py-1 w-full" /></td>
                        <td className="p-2 text-right font-data">{fmtFCFA(lineTotal)}</td>
                        <td className="text-center"><button onClick={() => delLine(i)} className="text-[var(--text-muted)] hover:text-[var(--alert,#c32a2a)]"><Trash2 size={14}/></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Totals */}
          {form.lines.length > 0 && (
            <div className="flex justify-end">
              <div className="w-64 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-[var(--text-muted)]">Sous-total</span><span className="font-data">{fmtFCFA(totals.subtotal)}</span></div>
                {form.apply_tva && <div className="flex justify-between"><span className="text-[var(--text-muted)]">TVA 18%</span><span className="font-data">{fmtFCFA(totals.tva)}</span></div>}
                <div className="flex justify-between text-lg pt-1.5 border-t border-[var(--border-line)]"><span className="font-bold">TOTAL</span><span className="font-data font-bold text-[var(--primary-dark,#8E3A6D)]">{fmtFCFA(totals.total)}</span></div>
              </div>
            </div>
          )}

          <div>
            <label className="label-mono block mb-1.5">Notes (optionnel)</label>
            <textarea rows={2} className="input-base" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Référence WhatsApp, contact, urgence..." />
          </div>
        </div>
        </div>
        <div className="shrink-0 flex justify-end gap-2 p-4 sm:p-5 border-t border-[var(--border-line)] bg-white">
          <button onClick={onClose} className="btn-ghost">Annuler</button>
          <button data-testid="submit-order" onClick={submit} className="btn-primary">Enregistrer en brouillon</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function CustomerOrders() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "user";
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [duplicateOrder, setDuplicateOrder] = useState(null);
  const [showRetro, setShowRetro] = useState(false);
  const [detail, setDetail] = useState(null); // selected order full
  const [detailPayments, setDetailPayments] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [pdfViewer, setPdfViewer] = useState(null); // {src, filename, kind} for in-app PDF viewer

  // Body scroll lock + Escape-to-close when any modal is open
  useEffect(() => {
    const isAnyOpen = !!detail || showNew || !!pdfViewer;
    if (!isAnyOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (pdfViewer) setPdfViewer(null);
        else if (detail) setDetail(null);
        else if (showNew) { setShowNew(false); setDuplicateOrder(null); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [detail, showNew, pdfViewer]);

  const load = async () => {
    const [o, p, c, s, dr] = await Promise.all([
      api.get("/customer-orders"), api.get("/products"),
      api.get("/customers"), api.get("/sites"),
      api.get("/drivers").catch(() => ({ data: [] })),
    ]);
    setOrders(o.data); setProducts(p.data); setCustomers(c.data); setSites(s.data);
    setDrivers(dr.data || []);
  };
  
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return orders;
    if (filter === "impayees") return orders.filter((o) => o.balance > 0 && o.status === "facturee");
    return orders.filter((o) => o.status === filter);
  }, [orders, filter]);

  const counts = useMemo(() => {
    const c = { all: orders.length, impayees: 0 };
    for (const k of Object.keys(STATUS)) c[k] = 0;
    for (const o of orders) {
      c[o.status] = (c[o.status] || 0) + 1;
      if (o.balance > 0 && o.status === "facturee") c.impayees++;
    }
    return c;
  }, [orders]);

  const transition = async (oid, action) => {
    try {
      const { data } = await api.post(`/customer-orders/${oid}/transition?action=${action}`);
      toast.success(`Commande → ${data.status}` + (data.delivery_number ? ` (${data.delivery_number})` : "") + (data.invoice_number ? ` (${data.invoice_number})` : ""));
      load();
      if (detail?.id === oid) openDetail(oid);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDetail = async (oid) => {
    try {
      const [{ data }, { data: pays }] = await Promise.all([
        api.get(`/customer-orders/${oid}`),
        api.get(`/customer-orders/${oid}/payments`).catch(() => ({ data: [] })),
      ]);
      setDetail(data);
      setDetailPayments(pays || []);
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openPdf = async (oid, kind) => {
    // In-app PDF viewer modal — works reliably on iOS standalone PWA (where
    // window.open() and blob: URL navigation are blocked) AND on desktop browsers.
    // iOS Safari requires data: URLs inside <iframe> to render PDFs natively;
    // every other browser handles blob: URLs fine.
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;
    const filename = `${kind === "bl" ? "BL" : "Facture"}-${oid.slice(0, 8)}.pdf`;
    const toastId = toast.loading("Génération du PDF…");
    try {
      const { data } = await api.get(`/customer-orders/${oid}/pdf/${kind}`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(data);
      // On iOS, also build a data URL so <iframe> renders the PDF natively
      // (blob: URLs in iframes are unreliable inside an iOS standalone PWA).
      let src = blobUrl;
      if (isIOS) {
        src = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = () => reject(r.error);
          r.readAsDataURL(data);
        });
      }
      toast.dismiss(toastId);
      setPdfViewer({ src, blobUrl, filename, kind });
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Cleanup the blob URL when the viewer closes
  useEffect(() => {
    if (!pdfViewer) return;
    return () => {
      if (pdfViewer.blobUrl) {
        try { URL.revokeObjectURL(pdfViewer.blobUrl); } catch (_) { /* noop */ }
      }
    };
  }, [pdfViewer]);

  const recordPayment = async (oid) => {
    const amt = window.prompt("Montant à encaisser (F) :");
    if (!amt) return;
    const method = window.prompt("Mode de paiement (livraison, virement, cheque) :", "livraison") || "livraison";
    try {
      const { data } = await api.post(`/customer-orders/${oid}/payment?amount=${Number(amt)}&method=${method}`);
      toast.success(`Encaissé : ${fmtFCFA(Number(amt))} · Reste ${fmtFCFA(data.balance)}`);
      load(); if (detail?.id === oid) openDetail(oid);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const assignDriver = async (oid, driverId) => {
    try {
      await api.post(`/customer-orders/${oid}/assign-driver`, { driver_id: driverId });
      const drv = drivers.find((d) => d.id === driverId);
      toast.success(`Chauffeur ${drv?.name || ""} assigné`);
      load(); if (detail?.id === oid) openDetail(oid);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (oid) => {
    if (!window.confirm("Supprimer cette commande brouillon ?")) return;
    try { await api.delete(`/customer-orders/${oid}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const duplicate = async (o) => {
    const { data } = await api.get(`/customer-orders/${o.id}`);
    setDuplicateOrder(data);
    setShowNew(true);
  };

  const NEXT_ACTION = {
    brouillon: { action: "confirmer", label: "Confirmer", icon: ClipboardCheck },
    confirmee: { action: "preparer", label: "Marquer préparée", icon: ClipboardCheck },
    preparee: { action: "livrer", label: "Livrer", icon: Truck },
    livree: { action: "facturer", label: "Facturer", icon: Receipt },
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Commandes clients"
        subtitle="Pharmacies · Hôpitaux · BL · Factures"
        testId="orders-header"
        actions={canEdit && (
          <div className="flex flex-wrap gap-2">
            <button data-testid="new-order-btn" onClick={() => { setDuplicateOrder(null); setShowNew(true); }} className="btn-primary flex items-center gap-2">
              <Plus size={14}/> Nouvelle commande
            </button>
            <button data-testid="retroactive-order-btn" onClick={() => setShowRetro(true)} className="btn-ghost flex items-center gap-2" title="Saisir une commande déjà livrée (génère BL + facture à la date d'origine, sans toucher au stock)">
              <History size={14}/> Saisie rétroactive
            </button>
          </div>
        )}
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter size={14} className="text-[var(--text-muted)]" />
        {["all", "brouillon", "confirmee", "preparee", "livree", "facturee", "impayees"].map((s) => {
          const lbl = s === "all" ? "Toutes" : s === "impayees" ? "Impayées" : STATUS[s].label;
          const active = filter === s;
          return (
            <button key={s} data-testid={`f-${s}`} onClick={() => setFilter(s)} className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-[var(--primary)] text-white border-[var(--primary)]" : "bg-white border-[var(--border-line)] text-[var(--text-muted)] hover:border-[var(--primary)]/50"}`}>
              {lbl} <span className="opacity-70 ml-1">({counts[s] || 0})</span>
            </button>
          );
        })}
      </div>

      <Surface testId="orders-table">
        {!filtered.length ? <Empty message="Aucune commande" /> : (
          <table className="data-table">
            <thead>
              <tr>
                <th>N°</th><th>Client</th><th>Date</th>
                <th className="text-right">Total</th><th className="text-right">Reste</th>
                <th>Conditions</th><th>Statut</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => {
                const baseSt = STATUS[o.status] || STATUS.brouillon;
                const st = (o.status === "facturee" && (o.balance ?? 0) <= 0.01)
                  ? { label: "PAYÉE", cls: "badge-success" }
                  : baseSt;
                const next = NEXT_ACTION[o.status];
                return (
                  <tr key={o.id} data-testid={`order-${o.id}`}>
                    <td className="font-data text-[var(--primary-dark,#8E3A6D)] font-semibold">{o.order_number}</td>
                    <td>{o.customer_name}</td>
                    <td className="font-data text-xs text-[var(--text-muted)]">{(o.created_at || "").slice(0, 10)}</td>
                    <td className="font-data text-right">{fmtFCFA(o.total)}</td>
                    <td className={`font-data text-right ${o.balance > 0 ? "text-[var(--alert,#c32a2a)] font-bold" : "text-[var(--primary-dark,#8E3A6D)]"}`}>{fmtFCFA(o.balance)}</td>
                    <td className="text-xs text-[var(--text-muted)]">{TERMS_LABEL[o.payment_terms] || o.payment_terms}</td>
                    <td><span className={st.cls}>{st.label}</span></td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button onClick={() => openDetail(o.id)} title="Détail" className="text-[var(--text-muted)] hover:text-[var(--primary)] p-1"><Eye size={14}/></button>
                        {canEdit && next && (
                          <button data-testid={`act-${o.id}`} onClick={() => transition(o.id, next.action)} className="btn-primary text-xs px-2 py-1 flex items-center gap-1">
                            <next.icon size={11}/> {next.label}
                          </button>
                        )}
                        {o.delivery_number && (
                          <button onClick={() => openPdf(o.id, "bl")} title="Voir BL" className="text-[var(--text-muted)] hover:text-[var(--primary)] p-1"><Truck size={14}/></button>
                        )}
                        {o.invoice_number && (
                          <button onClick={() => openPdf(o.id, "facture")} title="Voir facture" className="text-[var(--text-muted)] hover:text-[var(--primary)] p-1"><FileText size={14}/></button>
                        )}
                        {canEdit && o.status === "facturee" && o.balance > 0 && (
                          <button data-testid={`pay-${o.id}`} onClick={() => recordPayment(o.id)} title="Encaisser" className="text-[var(--primary)] hover:text-[var(--primary-dark)] p-1"><Banknote size={14}/></button>
                        )}
                        {canEdit && (
                          <button onClick={() => duplicate(o)} title="Dupliquer" className="text-[var(--text-muted)] hover:text-[var(--primary)] p-1"><Copy size={14}/></button>
                        )}
                        {canEdit && o.status === "brouillon" && (
                          <button onClick={() => remove(o.id)} title="Supprimer" className="text-[var(--text-muted)] hover:text-[var(--alert,#c32a2a)] p-1"><Trash2 size={14}/></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Surface>

      {/* Detail modal — invoice-style, internal scroll, sticky header + action footer */}
      {detail && (() => {
        const next = NEXT_ACTION[detail.status];
        const baseSt = STATUS[detail.status] || STATUS.brouillon;
        // Dynamic badge: "PAYÉE" overrides "FACTURÉE" when balance == 0
        const st = (detail.status === "facturee" && detail.balance <= 0.01)
          ? { label: "PAYÉE", cls: "badge-success" }
          : baseSt;
        return createPortal(
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex justify-center sm:p-4"
          onClick={() => setDetail(null)}
          data-testid="order-detail-overlay"
        >
          <div
            className="bg-white w-full sm:max-w-4xl shadow-2xl flex flex-col sm:rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="order-detail"
          >
            {/* Sticky header */}
            <div className="shrink-0 bg-white flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-[var(--border-line)]">
              <div className="min-w-0 flex-1 pr-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-extrabold text-lg sm:text-2xl truncate">{detail.order_number}</span>
                  <span className={st.cls}>{st.label}</span>
                </div>
                <div className="label-mono mt-1 truncate">{detail.customer?.name} · {(detail.created_at || "").slice(0, 10)}</div>
              </div>
              <button
                onClick={() => setDetail(null)}
                className="w-10 h-10 rounded-full hover:bg-[var(--bg-soft,#f7f7f7)] flex items-center justify-center text-[var(--text-muted)] shrink-0 no-print"
                data-testid="close-detail"
                aria-label="Fermer"
              >
                <X size={22}/>
              </button>
            </div>

            {/* Scrollable body — invoice style */}
            <div className="flex-1 overflow-y-auto bg-[var(--bg-soft,#f7f7f7)]/40">
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
                {/* Customer card + meta side-by-side on desktop */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
                  {/* Customer */}
                  {detail.customer && (
                    <div className="lg:col-span-2 bg-white rounded-xl p-4 sm:p-5 border border-[var(--border-line)]" data-testid="customer-info-card">
                      <div className="label-mono mb-3">Client</div>
                      <div className="font-heading font-bold text-base sm:text-lg mb-2">{detail.customer.name || "—"}</div>
                      <div className="text-xs text-[var(--text-muted)] capitalize mb-3">{detail.customer.type || "—"}</div>
                      <div className="space-y-1.5 text-sm">
                        {detail.customer.phone && (
                          <div className="flex items-center gap-2">
                            <Phone size={14} className="text-[var(--text-muted)] shrink-0"/>
                            <a href={`tel:${detail.customer.phone}`} className="font-data text-[var(--primary-dark,#8E3A6D)] hover:underline">{detail.customer.phone}</a>
                          </div>
                        )}
                        {detail.customer.email && (
                          <div className="flex items-center gap-2 min-w-0">
                            <Mail size={14} className="text-[var(--text-muted)] shrink-0"/>
                            <a href={`mailto:${detail.customer.email}`} className="font-data text-[var(--primary-dark,#8E3A6D)] hover:underline truncate">{detail.customer.email}</a>
                          </div>
                        )}
                        {detail.customer.address && (
                          <div className="flex items-start gap-2">
                            <MapPin size={14} className="text-[var(--text-muted)] shrink-0 mt-0.5"/>
                            <span>{detail.customer.address}</span>
                          </div>
                        )}
                      </div>
                      {(detail.payment_method || detail.notes || detail.source) && (
                        <div className="mt-3 pt-3 border-t border-[var(--border-line)] space-y-1.5 text-sm">
                          {detail.payment_method && <div><span className="text-[var(--text-muted)]">Paiement souhaité : </span><span className="capitalize font-medium">{detail.payment_method}</span></div>}
                          {detail.source && <div><span className="text-[var(--text-muted)]">Origine : </span><span className="capitalize">{detail.source === "web" ? "Catalogue web" : detail.source}</span></div>}
                          {detail.notes && <div><span className="text-[var(--text-muted)]">Notes : </span><span className="italic">{detail.notes}</span></div>}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Meta */}
                  <div className="bg-white rounded-xl p-4 sm:p-5 border border-[var(--border-line)] space-y-3 text-sm">
                    <div>
                      <div className="label-mono mb-1">Conditions</div>
                      <div className="font-medium">{TERMS_LABEL[detail.payment_terms] || detail.payment_terms}</div>
                    </div>
                    <div>
                      <div className="label-mono mb-1">Bon de livraison</div>
                      <div className="font-data">{detail.delivery_number || <span className="text-[var(--text-muted)]">—</span>}</div>
                    </div>
                    <div>
                      <div className="label-mono mb-1">Facture</div>
                      <div className="font-data">{detail.invoice_number || <span className="text-[var(--text-muted)]">—</span>}</div>
                    </div>
                    {/* Driver assignment (visible when ready to be delivered) */}
                    {canEdit && (detail.status === "preparee" || detail.status === "confirmee") && (
                      <div className="pt-2 border-t border-[var(--border-line)] no-print">
                        <div className="label-mono mb-1 flex items-center gap-1"><Truck size={11}/>Chauffeur livreur</div>
                        {detail.assigned_driver_name && (
                          <div className="text-xs text-[var(--primary-dark,#8E3A6D)] font-semibold mb-1.5">✓ {detail.assigned_driver_name}</div>
                        )}
                        {drivers.length === 0 ? (
                          <a href="/livraisons" className="text-xs text-[var(--primary-dark,#8E3A6D)] hover:underline">+ Ajouter un chauffeur</a>
                        ) : (
                          <select
                            data-testid="detail-assign-driver"
                            className="input-base text-sm"
                            value={detail.assigned_driver_id || ""}
                            onChange={(e) => e.target.value && assignDriver(detail.id, e.target.value)}
                          >
                            <option value="">— {detail.assigned_driver_id ? "Changer" : "Assigner"} chauffeur —</option>
                            {drivers.filter((d) => d.active).map((d) => (
                              <option key={d.id} value={d.id}>{d.name}{d.phone ? ` (${d.phone})` : ""}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] pt-1 border-t border-[var(--border-line)]">
                      <Calendar size={12}/>
                      <span>{(detail.created_at || "").slice(0, 10)}</span>
                    </div>
                  </div>
                </div>

                {/* Items table — invoice style */}
                <div className="bg-white rounded-xl border border-[var(--border-line)] overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[var(--bg-soft,#f7f7f7)] border-b border-[var(--border-line)]">
                          <th className="text-left p-3 label-mono">Produit</th>
                          <th className="text-right p-3 label-mono whitespace-nowrap w-16">Qté</th>
                          <th className="text-right p-3 label-mono whitespace-nowrap w-28">PU</th>
                          <th className="text-right p-3 label-mono whitespace-nowrap w-32">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.lines.map((l, i) => (
                          <tr key={l.product_id || l.product_sku || `detail-line-${i}`} className="border-t border-[var(--border-line)]">
                            <td className="p-3">
                              <div className="font-medium">{l.product_name}</div>
                              <div className="font-data text-xs text-[var(--text-muted)]">{l.product_sku}</div>
                            </td>
                            <td className="p-3 text-right font-data">{l.quantity}</td>
                            <td className="p-3 text-right font-data whitespace-nowrap">{fmtFCFA(l.unit_price)}</td>
                            <td className="p-3 text-right font-data font-semibold whitespace-nowrap">{fmtFCFA(l.line_total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Totals box */}
                  <div className="border-t-2 border-[var(--border-line)] bg-[var(--bg-soft,#f7f7f7)]/60 p-4 flex justify-end">
                    <div className="w-full sm:w-72 space-y-1.5 text-sm">
                      <div className="flex justify-between"><span className="text-[var(--text-muted)]">Sous-total</span><span className="font-data">{fmtFCFA(detail.subtotal)}</span></div>
                      {detail.apply_tva && <div className="flex justify-between"><span className="text-[var(--text-muted)]">TVA 18%</span><span className="font-data">{fmtFCFA(detail.tva)}</span></div>}
                      <div className="flex justify-between text-base sm:text-lg pt-2 border-t border-[var(--border-line)]">
                        <span className="font-bold">TOTAL</span>
                        <span className="font-data font-bold text-[var(--primary-dark,#8E3A6D)]">{fmtFCFA(detail.total)}</span>
                      </div>
                      <div className="flex justify-between pt-1"><span className="text-[var(--text-muted)]">Encaissé</span><span className="font-data">{fmtFCFA(detail.amount_paid)}</span></div>
                      <div className="flex justify-between pt-1 border-t border-dashed border-[var(--border-line)]">
                        <span className="font-bold">Reste dû</span>
                        <span className={`font-data font-bold ${detail.balance > 0 ? "text-[var(--alert,#c32a2a)]" : "text-[var(--primary-dark,#8E3A6D)]"}`}>{fmtFCFA(detail.balance)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery proof (signature + photo) — visible after delivery */}
                {(detail.delivery_signature || detail.delivery_proof_photo || detail.delivered_by_driver_name) && (
                  <div className="bg-white rounded-xl border border-[var(--border-line)] p-4 sm:p-5" data-testid="delivery-proof-card">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <div className="label-mono flex items-center gap-1.5"><Truck size={12}/>Preuve de livraison</div>
                      {detail.delivered_by_driver_name && (
                        <div className="text-xs text-[var(--text-muted)]">Livré par <span className="font-semibold text-[var(--text-base)]">{detail.delivered_by_driver_name}</span> · {(detail.delivered_at || "").slice(0, 16).replace("T", " à ")}</div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {detail.delivery_signature && (
                        <div>
                          <div className="text-xs text-[var(--text-muted)] mb-1.5">Signature client</div>
                          <a href={detail.delivery_signature} target="_blank" rel="noreferrer" className="block bg-white border-2 border-dashed border-[var(--border-line)] rounded-lg p-2 hover:border-[var(--primary)]">
                            <img src={detail.delivery_signature} alt="Signature client" className="w-full h-32 object-contain" data-testid="delivery-signature-img" />
                          </a>
                        </div>
                      )}
                      {detail.delivery_proof_photo && (
                        <div>
                          <div className="text-xs text-[var(--text-muted)] mb-1.5">Photo de livraison</div>
                          <a href={detail.delivery_proof_photo} target="_blank" rel="noreferrer" className="block">
                            <img src={detail.delivery_proof_photo} alt="Photo livraison" className="w-full h-32 object-cover rounded-lg border border-[var(--border-line)]" data-testid="delivery-photo-img" />
                          </a>
                        </div>
                      )}
                    </div>
                    {detail.delivery_notes && (
                      <div className="mt-3 pt-3 border-t border-[var(--border-line)] text-sm">
                        <span className="text-[var(--text-muted)] text-xs">Note du chauffeur : </span>
                        <span className="italic">{detail.delivery_notes}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Payments history — visible if there are any payments */}
                {detailPayments && detailPayments.length > 0 && (
                  <div className="bg-white rounded-xl border border-[var(--border-line)] p-4 sm:p-5" data-testid="payments-history-card">
                    <div className="label-mono mb-3 flex items-center gap-1.5"><Banknote size={12}/>Historique des paiements ({detailPayments.length})</div>
                    <div className="space-y-2">
                      {detailPayments.map((p) => (
                        <div key={p.id} className="flex items-center justify-between gap-3 p-2.5 bg-[var(--bg-soft,#f7f7f7)]/50 rounded-lg text-sm" data-testid={`payment-row-${p.id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-data text-xs text-[var(--text-muted)]">{(p.created_at || "").slice(0, 16).replace("T", " à ")}</span>
                              <span className="text-xs px-1.5 py-0.5 bg-white border border-[var(--border-line)] rounded capitalize">{(p.method || "").replace("_", " ")}</span>
                            </div>
                            <div className="text-xs mt-0.5">
                              {p.by_driver_name ? (
                                <span className="text-[var(--primary-dark,#8E3A6D)] font-medium">🚚 Chauffeur · {p.by_driver_name}</span>
                              ) : p.by_user_name ? (
                                <span className="text-[var(--text-muted)]">👤 {p.by_user_name}</span>
                              ) : (
                                <span className="text-[var(--text-muted)]">Système</span>
                              )}
                              {p.note && <span className="text-[var(--text-muted)] italic ml-2">— {p.note}</span>}
                            </div>
                          </div>
                          <div className="font-data font-bold text-[var(--primary-dark,#8E3A6D)] shrink-0">+{fmtFCFA(p.amount)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Payment block (impayées) */}
                {detail.invoice_number && detail.balance > 0 && (
                  <PaymentBlock
                    order={detail}
                    onPaid={async () => {
                      const [{ data }, { data: pays }] = await Promise.all([
                        api.get(`/customer-orders/${detail.id}`),
                        api.get(`/customer-orders/${detail.id}/payments`).catch(() => ({ data: [] })),
                      ]);
                      setDetail(data);
                      setDetailPayments(pays || []);
                      load();
                    }}
                  />
                )}
                {detail.invoice_number && detail.balance <= 0 && (
                  <div className="bg-[var(--primary-soft,#F8E8F1)] border border-[var(--primary,#C2569B)]/30 rounded-xl p-4 text-sm text-[var(--primary-dark,#8E3A6D)] font-semibold flex items-center gap-2">
                    <CheckCircle2 size={18}/> Facture entièrement réglée
                  </div>
                )}
              </div>
            </div>

            {/* Sticky action footer */}
            <div className="shrink-0 bg-white border-t border-[var(--border-line)] px-3 sm:px-6 py-3 flex flex-wrap items-center gap-2 justify-end no-print" data-testid="detail-action-footer">
              {detail.delivery_number && (
                <button
                  onClick={() => openPdf(detail.id, "bl")}
                  className="btn-ghost flex items-center gap-1.5 text-sm"
                  data-testid="detail-view-bl"
                  title="Voir le bon de livraison"
                >
                  <Truck size={14}/> <span className="hidden sm:inline">Voir BL</span><span className="sm:hidden">BL</span>
                </button>
              )}
              {detail.invoice_number && (
                <button
                  onClick={() => openPdf(detail.id, "facture")}
                  className="btn-ghost flex items-center gap-1.5 text-sm"
                  data-testid="detail-view-facture"
                  title="Voir la facture"
                >
                  <FileText size={14}/> <span className="hidden sm:inline">Voir facture</span><span className="sm:hidden">Facture</span>
                </button>
              )}
              {canEdit && (
                <button
                  onClick={() => { duplicate(detail); setDetail(null); }}
                  className="btn-ghost flex items-center gap-1.5 text-sm no-print"
                  data-testid="detail-duplicate"
                  title="Dupliquer cette commande"
                >
                  <Copy size={14}/> <span className="hidden sm:inline">Dupliquer</span>
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); window.print(); }}
                className="btn-ghost flex items-center gap-1.5 text-sm no-print"
                data-testid="detail-print"
                title="Imprimer cette commande"
              >
                <Printer size={14}/> <span className="hidden sm:inline">Imprimer</span>
              </button>
              <div className="flex-1" />
              <button onClick={() => setDetail(null)} className="btn-ghost text-sm" data-testid="detail-close-btn">Fermer</button>
              {canEdit && next && (
                <button
                  data-testid="detail-next-action"
                  onClick={() => transition(detail.id, next.action)}
                  className="btn-primary flex items-center gap-1.5 text-sm"
                >
                  <next.icon size={14}/> {next.label}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
        );
      })()}

      {showNew && (
        <NewOrderForm
          products={products} customers={customers} sites={sites}
          duplicate={duplicateOrder}
          onClose={() => { setShowNew(false); setDuplicateOrder(null); }}
          onCreated={load}
        />
      )}

      {pdfViewer && createPortal(
        <div
          className="fixed inset-0 z-[120] bg-[#1f2937] flex flex-col"
          data-testid="pdf-viewer-modal"
        >
          <div className="shrink-0 flex items-center gap-2 px-3 py-2 bg-[#111827] text-white border-b border-black/30">
            <div className="font-data text-sm truncate flex-1">
              {pdfViewer.kind === "bl" ? "Bon de livraison" : "Facture"} · {pdfViewer.filename}
            </div>
            <a
              href={pdfViewer.src}
              download={pdfViewer.filename}
              className="px-3 py-1.5 rounded-md bg-[#C2569B] hover:bg-[#8E3A6D] text-white text-sm font-medium flex items-center gap-1.5"
              data-testid="pdf-viewer-download"
            >
              <FileText size={14}/> Télécharger
            </a>
            <button
              onClick={() => setPdfViewer(null)}
              className="px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm flex items-center gap-1.5"
              data-testid="pdf-viewer-close"
              aria-label="Fermer"
            >
              <X size={14}/> Fermer
            </button>
          </div>
          <iframe
            src={pdfViewer.src}
            title={pdfViewer.filename}
            className="flex-1 w-full border-0 bg-white"
            data-testid="pdf-viewer-iframe"
          />
        </div>,
        document.body
      )}

      <RetroactiveOrderModal
        open={showRetro}
        onClose={() => setShowRetro(false)}
        onCreated={() => load()}
      />
    </div>
  );
}



// ---- Payment block (saisie d'un encaissement) ----
function PaymentBlock({ order, onPaid }) {
  const [amount, setAmount] = useState(order.balance || 0);
  const [method, setMethod] = useState("especes");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const a = Number(amount);
    if (!a || a <= 0) return toast.error("Montant invalide");
    if (a > order.balance + 0.01) return toast.error("Montant supérieur au reste dû");
    setSaving(true);
    try {
      await api.post(`/customer-orders/${order.id}/payment`, { amount: a, method, note });
      toast.success(`Paiement de ${a.toLocaleString("fr-FR")} F enregistré`);
      onPaid();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const fillFull = () => setAmount(order.balance);

  return (
    <div className="border-2 border-dashed border-[var(--primary,#C2569B)]/40 rounded-lg p-4 bg-[var(--primary-soft,#F8E8F1)]/40" data-testid="payment-block">
      <div className="flex items-center gap-2 mb-3">
        <Banknote size={18} className="text-[var(--primary-dark,#8E3A6D)]" />
        <div className="font-heading font-bold text-base">Enregistrer un paiement</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div className="sm:col-span-1">
          <label className="label-mono mb-1.5 block">Montant (F CFA)</label>
          <div className="flex gap-1">
            <input
              type="number"
              data-testid="payment-amount"
              className="input-base flex-1 font-data"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
              max={order.balance}
            />
            <button type="button" onClick={fillFull} className="btn-ghost text-xs px-2" title="Régler la totalité">Tout</button>
          </div>
        </div>
        <div>
          <label className="label-mono mb-1.5 block">Mode</label>
          <select data-testid="payment-method" className="input-base" value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="especes">Espèces (à la livraison)</option>
            <option value="virement">Virement bancaire</option>
            <option value="cheque">Chèque</option>
            <option value="autre">Autre</option>
          </select>
        </div>
        <div>
          <label className="label-mono mb-1.5 block">Référence (optionnel)</label>
          <input
            type="text"
            data-testid="payment-note"
            className="input-base"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="N° chèque, référence virement..."
          />
        </div>
      </div>
      <button data-testid="confirm-payment" onClick={submit} disabled={saving} className="btn-primary mt-3 flex items-center gap-2">
        <CheckCircle2 size={14}/>{saving ? "..." : "Valider le paiement"}
      </button>
    </div>
  );
}
